// TESSERAX — MONTÉE D'INSTABILITÉ (pression croissante de fin de partie).
//
// ⚠ STATUT (2026-07) : la montée est GELÉE (directive produit) puis DÉBRANCHÉE des moteurs —
// rise:false sur tous les modes (core/modes), doRise/riseRng/parasites retirés de main.ts et
// core/sim. Dans ce module, SEULE linesPerCharge (économie de charge de Bascule) reste branchée ;
// riseIntervalMs/riseFill/parasiteFraction/survivalScale/… sont conservées PURES ET TESTÉES à
// titre d'archive (elles ne doivent PAS être rebranchées — simplifier, jamais étendre).
//
// PROBLÈME corrigé (retour de partie réel : Profondeur 44, 3 h 14, 1089 lignes,
// 116 Bascules, 4,4 M points) : la seule menace — les trous sous la pile — est
// ANNULÉE gratuitement par l'outil principal du joueur (la Bascule répare les
// trous), et la difficulté ne monte que sur un axe (vitesse de chute) qui plafonne
// dès la Profondeur ~14. Conséquence : la puissance accumulée (charges, Zone,
// multiplicateurs de reliques) dépasse la menace → les runs ne meurent plus du
// défi mais de la fatigue, et durent indéfiniment.
//
// SOLUTION (précédents du genre) :
//  - Panel de Pon / Tetris Attack « Endless » : la pile MONTE seule, de plus en
//    plus vite ; un combo n'offre qu'un « Stop » temporaire, jamais l'immunité.
//  - Risk of Rain : le TEMPS écoulé fait monter la difficulté → punit l'attentisme.
//  - Tetris Grand Master (Death) : ce n'est pas la gravité qui se durcit mais les
//    FENÊTRES (lock delay, ARE) qui se resserrent.
// On ajoute donc une menace qui monte avec la Profondeur ET le temps, que la
// Bascule peut soulager mais jamais distancer pour toujours, et on resserre la
// régénération des charges en profondeur.
//
// Module PUR : ni THREE ni DOM, pas de Math.random. La rangée montante tire ses
// cases d'une RNG passée en argument (la RNG de run) → le défi du jour reste
// reproductible à l'identique partout.
//
// STATUT (2026-06) : la montée d'instabilité est GELÉE — DÉSACTIVÉE dans tous les modes
// (cf. core/modes.rise=false) ; `doRise` dans main.ts n'est donc jamais appelé. Le code
// ci-dessous est conservé INTACT (frozen — cf. CLAUDE.md + docs/duel-1v1.md), mais SEUL
// `chargeRegen` est réellement câblé au jeu aujourd'hui ; la cadence/remplissage/scellage
// et l'économie de survie (parasiteFraction/survivalScale) n'ont plus rien à traiter tant
// qu'aucun mode ne réactive la montée.
import { GRID } from './constants';

// Avant ce palier de Profondeur, AUCUNE montée : le début reste accueillant et
// laisse apprendre placement / Bascule / Zone (cf. revue onboarding).
export const RISE_START_LEVEL = 5;

// Réglages de la cadence g(L,t) = clamp(startMs − (L−L0)·perLevel − t·perSec, floorMs).
export interface RiseTuning { startMs: number; perLevel: number; perSec: number; floorMs: number; }

// Cadence initiale ~20 s, raccourcie par la Profondeur ET par le temps écoulé,
// plancher ~3,4 s (cadence maximale). À ce plancher, suivre le rythme exige des
// clears quasi continus : une bonne run meurt en ~20–40 min, plus en 3 h.
export const RISE_TUNING: RiseTuning = { startMs: 20000, perLevel: 360, perSec: 28, floorMs: 3400 };

// Intervalle (ms) avant la prochaine montée. Infinity tant que L < RISE_START_LEVEL.
// Monotone décroissant en L et en temps ; jamais sous floorMs.
export function riseIntervalMs(level: number, elapsedSec: number, tuning: RiseTuning = RISE_TUNING): number {
  if (level < RISE_START_LEVEL) return Infinity;
  const t = tuning;
  return Math.max(t.floorMs, t.startMs - (level - RISE_START_LEVEL) * t.perLevel - Math.max(0, elapsedSec) * t.perSec);
}

// Nombre de COLONNES (x,z) que la montée fait grimper d'un cran. Borné pour rester
// JOUABLE : jamais toutes les colonnes (sinon montée d'un étage plein = brutal),
// jamais aucune. Monte légèrement avec la Profondeur (~16 colonnes au début → ~25
// en profondeur, sur les 49 d'un puits 7×7).
export function riseFillCount(level: number): number {
  const cells = GRID * GRID;                          // 49 sur un puits 7×7
  const n = Math.round(cells * Math.min(0.5, 0.3 + level * 0.005));
  return Math.max(1, Math.min(cells - 1, n));         // au moins 1 colonne, jamais toutes
}

// Choisit les cases [x,z] pleines de la rangée montante via la RNG de run (mélange
// de Fisher-Yates → déterministe pour une même graine). `rand` = mulberry32 de la run.
export function riseFill(rand: () => number, count: number): number[][] {
  const cells: number[][] = [];
  for (let x = 0; x < GRID; x++) for (let z = 0; z < GRID; z++) cells.push([x, z]);
  for (let i = cells.length - 1; i > 0; i--) { const j = Math.floor(rand() * (i + 1)); const tmp = cells[i]; cells[i] = cells[j]; cells[j] = tmp; }
  return cells.slice(0, Math.max(0, Math.min(cells.length, count)));
}

// COÛT D'UNE CHARGE DE BASCULE, en LIGNES effacées — monte avec la Profondeur.
//
// L'ancien `chargeRegen` rendait une charge pour tout étage plein ou tout clear ≥3 lignes :
// il ne se tarissait jamais (la pile restait éternellement réparable → « tilt infini »).
// Exiger une grille pleine en profondeur serait à l'inverse quasi impossible (un étage 7×7
// complet est rarissime). On passe donc à une BANQUE DE LIGNES : main.ts accumule les lignes
// effacées (pondérées par l'économie de survie) et débloque UNE charge chaque fois que le
// cumul atteint `linesPerCharge(level)`. Plus on est profond, plus une charge coûte de lignes
// → la Bascule redevient une ressource rare et tactique. Pur / déterministe (fonction du seul
// niveau) → déterminisme du défi du jour intact.
//
// Progression : L1→3, L6→4, L11→5, L16→6, L21→7, L26+→8 lignes par charge. Monotone
// non-décroissant, plafonné. Encore accueillant en début de partie (la 1re charge tombe
// en quelques effacements), franchement sec en grande Profondeur.
//
// RÉÉQUILIBRAGE (2026-06, retour Directeur « débloquer une charge est bien trop simple ») :
// la Bascule ET le Pli (`doFold`) puisent dans la MÊME réserve `charges` ; une charge qui
// coûtait 2 lignes au départ (un simple double) en rendait l'usage quasi gratuit. On relève
// le coût plancher 2→3, on resserre le palier (tous les 6→5 niveaux) et on remonte le plafond
// 6→8 : la Bascule/le Pli redeviennent une ressource que l'on MÉRITE et que l'on dépense, pas
// un robinet. Pur/déterministe ⇒ le sim anti-triche (core/sim) reste bit-exact avec le moteur vif.
//
// SECOND PASSAGE (2026-06, retour joueur « trop de charges pour la Bascule ET l'ana/kata ») :
// la réserve PARTAGÉE se remplissait encore plus vite qu'elle ne se vidait en milieu de partie.
// On remonte d'un cran toute la courbe — plancher 3→4, palier tous les 5→4 niveaux, plafond 8→9 —
// pour que chaque charge (Bascule ou Pli) reste MÉRITÉE plutôt qu'abondante. Mêmes garanties de
// pureté/déterminisme (core/sim importe cette fonction → parité automatique).
export const LINES_PER_CHARGE_BASE = 4;
export const LINES_PER_CHARGE_MAX = 9;
export function linesPerCharge(level: number): number {
  const L = Math.max(1, Number.isFinite(level) ? Math.floor(level) : 1);   // garde NaN → coût borné
  return Math.min(LINES_PER_CHARGE_MAX, LINES_PER_CHARGE_BASE + Math.floor((L - 1) / 4));
}

// ÉCONOMIE DE SURVIE vs MONTÉE — la faille corrigée ici.
//
// PROBLÈME : la montée pose des cases parasites et SUR-REMPLIT le puits ; la pièce
// SUIVANTE du joueur complète alors des rangées « offertes » par la montée. Ce clear
// est PROVOQUÉ par le joueur (non `passive`) → il reverse pleinement charge de Bascule
// + jauge de Zone. La menace finance donc elle-même son antidote (Bascule/Zone infinies,
// pile éternellement réparée). Le garde `passive` de main.ts ne couvrait que la
// résolution PROPRE de la montée, pas ce paiement décalé d'un coup.
//
// RÈGLE : les RESSOURCES DE SURVIE (charge, jauge) ne sont financées que par les cases
// que le JOUEUR a posées, pas par les cases parasites que la montée a offertes. Le SCORE
// (et le juice) restent intacts : effacer la montée reste gratifiant et rapporte des
// points — ça cesse seulement de RECHARGER les outils.

// Couleur des blocs parasites posés par la montée (cf. main.INSTAB_COLOR). Centralisée
// ici pour que la règle d'économie et le rendu partagent la même teinte inerte.
export const INSTAB_COLOR = 0x5d6b86;
// Couleur des blocs « scellés » (parasites ancrés) que la Bascule ne peut pas déplacer.
export const SEAL_COLOR = 0x37415c;

// Combien de cases la montée SCELLE (ancre anti-Bascule), en profondeur seulement et
// avec parcimonie : la menace devient un vrai trou que le tassement ne comble pas, à
// vider par empilement. 0 avant le palier (début/ milieu de partie épargnés). Pur.
export const SEAL_START_LEVEL = 12;
export function sealCountForRise(level: number): number {
  if (level < SEAL_START_LEVEL) return 0;
  return level < 22 ? 1 : 2;
}

// Part [0..1] de cases parasites dans un lot de cases effacées `[x,y,z,color]`.
// 0 = clear 100 % joueur (récompense pleine) ; 1 = clear 100 % offert par la montée.
// `parasiteColors` accepte une couleur OU une liste (parasites + scellés tous deux
// inertes pour l'économie). Cases sans couleur (4ᵉ champ absent) → comptées « joueur ».
// PERF-CORE-006 : indexOf remplacé par des comparaisons === pour éviter l'allocation
// implicite et le scan linéaire — le cas tableau à 2 éléments (INSTAB_COLOR/SEAL_COLOR)
// est traité en ligne avec c0/c1.
export function parasiteFraction(clearedCells: number[][], parasiteColors: number | number[] = INSTAB_COLOR): number {
  if (!clearedCells || clearedCells.length === 0) return 0;
  let par = 0;
  if (Array.isArray(parasiteColors)) {
    const c0 = parasiteColors[0], c1 = parasiteColors[1];
    for (let i = 0; i < clearedCells.length; i++) { const c = clearedCells[i][3]; if (c === c0 || c === c1) par++; }
  } else {
    const c0 = parasiteColors;
    for (let i = 0; i < clearedCells.length; i++) if (clearedCells[i][3] === c0) par++;
  }
  return par / clearedCells.length;
}

// Multiplicateur appliqué aux gains de SURVIE (charge + jauge) selon la part parasite.
// survivalScale(0) = 1 (jeu normal inchangé) ; survivalScale(1) = 0 (montée pure ne
// reverse rien). Courbe légèrement raidie (>1) → un clear majoritairement parasite ne
// rend quasi rien, sans pénaliser un clear surtout construit par le joueur. Monotone
// décroissante. Pure → réglable en un seul endroit.
export const SURVIVAL_PARASITE_EXP = 1.25;
export function survivalScale(parasiteFraction: number, exp: number = SURVIVAL_PARASITE_EXP): number {
  const f = Math.max(0, Math.min(1, parasiteFraction));
  return Math.pow(1 - f, exp);
}
